import { useReducer } from 'react';

import type { DisplayMode } from 'src/components';

interface ViewState {
  displayMode: DisplayMode;
  missingMatchesOnly: boolean;
  unconfirmedRelationshipsOnly: boolean;
}

type ViewAction =
  | { mode: DisplayMode; type: 'setDisplayMode' }
  | { type: 'toggleUnconfirmedOnly' }
  | { type: 'toggleMissingMatchesOnly' };

const DEFAULT_INITIAL_STATE: ViewState = {
  displayMode: 'flat',
  missingMatchesOnly: false,
  unconfirmedRelationshipsOnly: false,
};

function viewReducer(state: ViewState, action: ViewAction): ViewState {
  switch (action.type) {
    case 'setDisplayMode': {
      if (action.mode === 'flat') {
        return {
          ...state,
          displayMode: 'flat',
          missingMatchesOnly: false,
          unconfirmedRelationshipsOnly: false,
        };
      }
      return {
        ...state,
        displayMode: 'relationship',
      };
    }

    case 'toggleUnconfirmedOnly': {
      const isCurrentlyActive =
        state.unconfirmedRelationshipsOnly && state.displayMode === 'relationship';
      if (isCurrentlyActive) {
        return {
          ...state,
          displayMode: 'flat',
          unconfirmedRelationshipsOnly: !state.unconfirmedRelationshipsOnly,
        };
      }
      return {
        ...state,
        displayMode: 'relationship',
        missingMatchesOnly: false,
        unconfirmedRelationshipsOnly: !state.unconfirmedRelationshipsOnly,
      };
    }

    case 'toggleMissingMatchesOnly': {
      const isCurrentlyActive = state.missingMatchesOnly && state.displayMode === 'relationship';
      if (isCurrentlyActive) {
        return {
          ...state,
          displayMode: 'flat',
          missingMatchesOnly: !state.missingMatchesOnly,
        };
      }
      return {
        ...state,
        displayMode: 'relationship',
        missingMatchesOnly: !state.missingMatchesOnly,
        unconfirmedRelationshipsOnly: false,
      };
    }

    default:
      return state;
  }
}

interface UseViewStateResult {
  dispatch: React.Dispatch<ViewAction>;
  displayMode: DisplayMode;
  missingMatchesOnly: boolean;
  unconfirmedRelationshipsOnly: boolean;
}

export function useViewState(): UseViewStateResult {
  const [state, dispatch] = useReducer(viewReducer, DEFAULT_INITIAL_STATE);

  return {
    dispatch,
    ...state,
  };
}

export type { ViewAction, ViewState };
